import { DateRangeSlicerDefaultSelection } from "@coeff/agent-types";

// ===========================================================================
// Pure date-range domain logic for the DateRangeSlicer picker.
//
// This module has NO React dependency on purpose: it is the testable core
// (bare-date math, preset construction, default_selection resolution) that
// both the picker component and useDashboardRuntime build on. Keep it pure.
// ===========================================================================

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

export interface BareRange {
  start: Date;
  end: Date;
}

export type SelectionUnit = "day" | "week" | "month" | "quarter" | "year";

export type BuilderMode = "last" | "this" | "between";
export type ThisUnit = "week" | "month" | "quarter" | "year";

// The canonical default_selection is the generated discriminated union straight from the
// backend schema (DateRangeSlicer.default_selection in web_dashboard_agent_utils.py). Aliased
// here — not re-declared — so the picker's resolver stays in lockstep with the schema and the
// compiler flags any drift, rather than a hand-maintained copy silently diverging. `last` = N
// complete periods, include_current appends the current in-progress one; `this` = current
// period to date, or through the period's last day with full_period (pipeline / forecast
// dashboards whose rows are dated later this period); `previous` = last N full calendar
// periods; `custom` = explicit ISO dates
// (null start = all history, null end = through today). Resolved to concrete dates by
// resolveSelection.
export type DateRangeSelection = DateRangeSlicerDefaultSelection;

// "All time" and open-ended bounds are committed as this sentinel-wide window (instead of
// the data's literal min/max) so generated SQL filters match every row — including rows
// outside the observed bounds and data added after load. A start at/below this (or an
// epoch/1970 date from a legacy default function) reads as "no lower bound"; an end at/above
// ALL_TIME_END reads as "no upper bound". Plain Dates keep every existing render function
// working unchanged: they just format/bind two dates like any other range.
export const ALL_TIME_START = new Date(1900, 0, 1);
export const ALL_TIME_END = new Date(9999, 11, 31);

// An endpoint is "open" (unbounded) when it hits a sentinel. year <= 1970 also catches the
// epoch value a broken get_default_value_js_function produces (SQLite DATE() on a non-ISO
// column returns NULL -> new Date(null) -> 1970) so the picker shows "Up to <end>" / "All
// time" instead of a literal "Jan 1, 1970".
export const isOpenStart = (d: Date): boolean => d.getFullYear() <= 1970;
export const isOpenEnd = (d: Date): boolean => d.getFullYear() >= 9999 || d.getFullYear() <= 1970;

export const DEFAULT_LAST_N = 30;
export const PANEL_WIDTH = 660;

// ---------------------------------------------------------------------------
// Date helpers (all dates are "bare dates": midnight local time)
// ---------------------------------------------------------------------------

/**
 * Date-only strings (e.g. "2025-12-01") are parsed as UTC midnight by the JS spec.
 * If UTC time is midnight, use UTC date parts. Otherwise use local date parts.
 * Values may come from the iframe realm, so avoid relying on instanceof alone.
 */
export const toBareDate = (value: Date | string): Date => {
  const timestamp = value instanceof Date ? value.getTime() : new Date(value).getTime();
  const d = new Date(timestamp);
  if (d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0) {
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

export const getToday = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

/** Normalize a DateRange prop to bare dates; null when missing or invalid. */
export const toBareRange = (range: DateRange): BareRange | null => {
  if (!range.startDate || !range.endDate) {
    return null;
  }
  const start = toBareDate(range.startDate);
  const end = toBareDate(range.endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return null;
  }
  return start.getTime() <= end.getTime() ? { start, end } : { start: end, end: start };
};

export const isSameDay = (d1: Date, d2: Date): boolean =>
  d1.getFullYear() === d2.getFullYear() &&
  d1.getMonth() === d2.getMonth() &&
  d1.getDate() === d2.getDate();

export const rangesEqual = (r1: BareRange, r2: BareRange): boolean =>
  isSameDay(r1.start, r2.start) && isSameDay(r1.end, r2.end);

export const minDate = (a: Date, b: Date): Date => (a.getTime() <= b.getTime() ? a : b);
export const maxDate = (a: Date, b: Date): Date => (a.getTime() >= b.getTime() ? a : b);

export const addDays = (d: Date, n: number): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

/** Whole days between start and end. */
export const diffDays = (start: Date, end: Date): number =>
  Math.round((end.getTime() - start.getTime()) / 86400000);

const startOfWeek = (d: Date) => addDays(d, -((d.getDay() + 6) % 7)); // Monday-first
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const startOfQuarter = (d: Date) => new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
const startOfYear = (d: Date) => new Date(d.getFullYear(), 0, 1);

/** Index of the month panel a date belongs to (months since year 0). */
export const monthIndexOf = (d: Date): number => d.getFullYear() * 12 + d.getMonth();

export const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const MONTHS_SHORT = MONTHS.map(m => m.slice(0, 3));
export const WEEKDAYS = ["M", "T", "W", "T", "F", "S", "S"];

export const formatDay = (d: Date): string =>
  `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;

export const formatRange = (r: BareRange): string => {
  if (isSameDay(r.start, r.end)) {
    return formatDay(r.start);
  }
  if (r.start.getFullYear() === r.end.getFullYear()) {
    return `${MONTHS_SHORT[r.start.getMonth()]} ${r.start.getDate()} – ${formatDay(r.end)}`;
  }
  return `${formatDay(r.start)} – ${formatDay(r.end)}`;
};

/** Parse a positive integer typed in the builder; null while the input is mid-edit. */
export const parseCount = (text: string): number | null => {
  if (!/^\d+$/.test(text)) {
    return null;
  }
  const n = parseInt(text, 10);
  return n >= 1 && n <= 3650 ? n : null;
};

export const dateToInputValue = (d: Date): string => {
  const yyyy = String(d.getFullYear()).padStart(4, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export const inputValueToDate = (value: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) {
    return null;
  }
  const year = parseInt(m[1], 10);
  const month = parseInt(m[2], 10) - 1;
  const day = parseInt(m[3], 10);
  const d = new Date(year, month, day);
  // Guard against JS's rollover for an invalid day-of-month (e.g. Feb 30 -> Mar 2).
  return d.getFullYear() === year && d.getMonth() === month && d.getDate() === day ? d : null;
};

// ---------------------------------------------------------------------------
// Period math
// ---------------------------------------------------------------------------

export const startOfThisUnit = (unit: ThisUnit, d: Date): Date => {
  if (unit === "week") {
    return startOfWeek(d);
  }
  if (unit === "month") {
    return startOfMonth(d);
  }
  if (unit === "quarter") {
    return startOfQuarter(d);
  }
  return startOfYear(d);
};

const startOfSelUnit = (unit: SelectionUnit, d: Date): Date =>
  unit === "day" ? new Date(d.getFullYear(), d.getMonth(), d.getDate()) : startOfThisUnit(unit, d);

/** Shift an aligned period-start by k periods (k negative to go earlier). */
const shiftUnitStart = (unit: SelectionUnit, start: Date, k: number): Date => {
  switch (unit) {
    case "day":
      return addDays(start, k);
    case "week":
      return addDays(start, 7 * k);
    case "month":
      return new Date(start.getFullYear(), start.getMonth() + k, 1);
    case "quarter":
      return new Date(start.getFullYear(), start.getMonth() + 3 * k, 1);
    case "year":
      return new Date(start.getFullYear() + k, 0, 1);
  }
};

export const clampRange = (r: BareRange, bounds: BareRange | null): BareRange => {
  if (!bounds) {
    return r;
  }
  let start = maxDate(r.start, bounds.start);
  let end = minDate(r.end, bounds.end);
  if (start.getTime() > end.getTime()) {
    // Range lies entirely outside the bounds; collapse to the nearest edge.
    const edge = r.end.getTime() < bounds.start.getTime() ? bounds.start : bounds.end;
    start = edge;
    end = edge;
  }
  return { start, end };
};

// "Last N <unit>" as N COMPLETE periods, optionally including the current (partial) one —
// the canonical default_selection `last` semantics (mirrors resolveSelection). For unit=day
// the period start is today, so includeCurrent=true ends at today (the "Last 30 days"
// behavior) and false ends at yesterday. For unit=month/quarter/year, false ends at the last
// complete period boundary and true extends through today.
export const computeLastRange = (
  n: number,
  unit: SelectionUnit,
  includeCurrent: boolean,
  bounds: BareRange | null
): BareRange => {
  const today = getToday();
  const curStart = startOfSelUnit(unit, today);
  const start = shiftUnitStart(unit, curStart, -n);
  const end = includeCurrent ? today : addDays(curStart, -1);
  return clampRange({ start, end }, bounds);
};

/**
 * Last day of the calendar period containing `d` (the day before the next period starts).
 * Twin of `_end_of_this_unit` in web_dashboard_date_range_selection.py — change both.
 */
export const endOfThisUnit = (unit: ThisUnit, d: Date): Date =>
  addDays(shiftUnitStart(unit, startOfThisUnit(unit, d), 1), -1);

// "This <unit>" to date, or through the period's last day with fullPeriod (mirrors
// resolveSelection's `this` + full_period: the pipeline / forecast default that must keep
// rows dated later this quarter).
export const computeThisRange = (
  unit: ThisUnit,
  bounds: BareRange | null,
  fullPeriod = false
): BareRange => {
  const today = getToday();
  const end = fullPeriod ? endOfThisUnit(unit, today) : today;
  return clampRange({ start: startOfThisUnit(unit, today), end }, bounds);
};

/**
 * Resolve the canonical `default_selection` intent into concrete dates, computed relative to
 * the CURRENT date at load time. `last` = N complete periods (include_current appends the
 * current partial one); `this` = current period to date, or through the period's last day
 * when full_period is set; `previous` = last N full calendar periods; `custom` = explicit
 * dates where a null bound is open (null start -> all history,
 * null end -> through today, both null -> the all-time sentinel window).
 */
export const resolveSelection = (sel: DateRangeSelection): DateRange => {
  const today = getToday();
  let start: Date;
  let end: Date;
  if (sel.kind === "last") {
    const curStart = startOfSelUnit(sel.unit, today);
    start = shiftUnitStart(sel.unit, curStart, -sel.n);
    end = sel.include_current ? today : addDays(curStart, -1);
  } else if (sel.kind === "this") {
    start = startOfThisUnit(sel.unit, today);
    // full_period keeps the rest of the period so rows dated later this quarter/month (deal
    // close dates, forecasts, renewals) are not silently cut off at today.
    end = sel.full_period ? endOfThisUnit(sel.unit, today) : today;
  } else if (sel.kind === "previous") {
    const curStart = startOfThisUnit(sel.unit, today);
    // n is optional in the schema (defaults to 1 = the immediately-prior period).
    start = shiftUnitStart(sel.unit, curStart, -(sel.n ?? 1));
    end = addDays(curStart, -1);
  } else {
    const parsedStart = sel.start ? inputValueToDate(sel.start) : null;
    const parsedEnd = sel.end ? inputValueToDate(sel.end) : null;
    start = parsedStart ?? new Date(ALL_TIME_START.getTime());
    // null end -> rolling through today; a fully-open custom (no start, no end) is "all time"
    // -> upper sentinel so the filter matches future-dated rows too.
    end = parsedEnd ?? (parsedStart ? today : new Date(ALL_TIME_END.getTime()));
  }
  return { startDate: new Date(start.getTime()), endDate: new Date(end.getTime()) };
};

// ---------------------------------------------------------------------------
// Presets & builder metadata
// ---------------------------------------------------------------------------

export interface Preset {
  id: string;
  label: string;
  range: BareRange;
  /** Set (with lastUnit) when the preset maps onto the "Last N <unit>" builder. */
  lastN?: number;
  lastUnit?: SelectionUnit;
  /**
   * Whether this preset's "Last N" shape includes the current in-progress period. Defaults to
   * true; "Last year" is the one preset that excludes it, which is exactly what makes it the
   * previous whole year rather than the year to date.
   */
  lastIncludeCurrent?: boolean;
  /** Set when the preset maps onto the "This <unit>" builder. */
  thisUnit?: ThisUnit;
  /** The "All time" preset: commits the open sentinel window rather than a literal range. */
  allTime?: boolean;
  /**
   * The "Custom" preset: switches to the "between" builder keeping the current draft range,
   * making the pick-your-own-dates path discoverable. Its `range` is a placeholder — never
   * committed and excluded from range-based preset attribution.
   */
  custom?: boolean;
}

// Units offered in the "Last" builder dropdown (matches the backend LastSelection.unit set).
export const LAST_UNITS: SelectionUnit[] = ["day", "week", "month", "quarter", "year"];

// Plural nouns for the "Last N <unit>" builder / descriptions ("Last 3 months").
export const LAST_UNIT_LABEL: Record<SelectionUnit, string> = {
  day: "days",
  week: "weeks",
  month: "months",
  quarter: "quarters",
  year: "years",
};

// Label for the include-current checkbox, phrased per unit ("Also include today" for days,
// "Include this month/quarter/year" otherwise) so it reads naturally next to the builder.
export const INCLUDE_CURRENT_LABEL: Record<SelectionUnit, string> = {
  day: "Also include today",
  week: "Include this week",
  month: "Include this month",
  quarter: "Include this quarter",
  year: "Include this year",
};

// "week" is intentionally omitted from the viewer-facing units (no preset, not in the
// "This" builder dropdown): it overlaps "Last 7 days" and is redundant. The week math
// above is kept so a server-provided default_selection of { kind: "this" | "last",
// unit: "week" } still resolves.
export const THIS_UNITS: ThisUnit[] = ["month", "quarter", "year"];

export const THIS_UNIT_LABEL: Record<ThisUnit, string> = {
  week: "Week",
  month: "Month",
  quarter: "Quarter",
  year: "Year",
};

// Checkbox label for the This builder's full-period modifier (the twin of
// INCLUDE_CURRENT_LABEL on the Last builder).
export const FULL_PERIOD_LABEL: Record<ThisUnit, string> = {
  week: "Through end of week",
  month: "Through end of month",
  quarter: "Through end of quarter",
  year: "Through end of year",
};

/** Keep a date filter's identity visible once its trigger starts showing the selected range. */
export const dateRangeTriggerText = (label: string, value: string): string => {
  if (!label || label === value) {
    return value;
  }
  return `${label}: ${value}`;
};

/**
 * Describe a range as the relative builder that would produce it — "Last N days" or
 * "This <unit>" when it ends today — so a relative selection reads as such instead of a
 * raw date range. Anything that doesn't end today (or a data-derived default) falls back
 * to the literal formatted range, i.e. a genuine "custom" range.
 */
export const describeRange = (r: BareRange, today: Date): string => {
  if (isSameDay(r.end, today)) {
    const unit = THIS_UNITS.find(u => isSameDay(r.start, startOfThisUnit(u, today)));
    if (unit) {
      return `This ${THIS_UNIT_LABEL[unit].toLowerCase()}`;
    }
    const days = diffDays(r.start, r.end);
    if (days >= 1) {
      return `Last ${days} days`;
    }
  }
  return formatRange(r);
};

/**
 * Human label for a structured selection — "Last 10 months", "This quarter", "Last year" —
 * derived from the selection's own kind/unit/count rather than reverse-mapped from resolved
 * dates. Returns null for `custom` (no relative phrasing; the caller formats the dates).
 */
export const selectionLabel = (sel: DateRangeSelection): string | null => {
  if (sel.kind === "last") {
    const unitLabel = LAST_UNIT_LABEL[sel.unit];
    if (sel.include_current) {
      return `Last ${sel.n} ${unitLabel}`;
    }
    // One complete period reads as "Last year" / "Last month", not "Last 1 complete years" —
    // the same phrasing `previous` uses below, which means the same thing.
    return sel.n === 1 ? `Last ${sel.unit}` : `Last ${sel.n} complete ${unitLabel}`;
  }
  if (sel.kind === "this") {
    const unit = THIS_UNIT_LABEL[sel.unit].toLowerCase();
    // A full-period default ends after today, so say so: a chip reading "This quarter" over a
    // window that runs to Sep 30 would look like the to-date preset it is not.
    return sel.full_period ? `This ${unit} (full)` : `This ${unit}`;
  }
  if (sel.kind === "previous") {
    const n = sel.n ?? 1;
    return n === 1 ? `Last ${sel.unit}` : `Last ${n} ${LAST_UNIT_LABEL[sel.unit]}`;
  }
  return null;
};

/** The unit-bearing builder inputs that survive a mode switch. */
export interface BuilderUnitState {
  lastNText: string;
  lastUnit: SelectionUnit;
  thisUnit: ThisUnit;
}

/**
 * Builder state after switching modes. Toggling This↔Last carries the selected time unit
 * across — "This year" → "Last 1 year", "Last 3 months" → "This month" — because the toggle
 * reads as re-phrasing the current selection, not as recalling whatever the other builder
 * last held (which lands on a baffling "Last 30 days" after "This year"). A Last-unit with
 * no "This" counterpart (day/week) and any switch involving "between" keep the state sticky.
 */
export const carryUnitAcrossModes = (
  prevMode: BuilderMode,
  nextMode: BuilderMode,
  state: BuilderUnitState
): BuilderUnitState => {
  if (prevMode === "this" && nextMode === "last") {
    return { ...state, lastNText: "1", lastUnit: state.thisUnit };
  }
  if (prevMode === "last" && nextMode === "this") {
    const unit = THIS_UNITS.find(u => u === state.lastUnit);
    return unit ? { ...state, thisUnit: unit } : state;
  }
  return state;
};

export interface BuilderSelectionState {
  isAllTime: boolean;
  mode: BuilderMode;
  lastN: number;
  lastUnit: SelectionUnit;
  includeCurrent: boolean;
  thisUnit: ThisUnit;
  /** "This <unit>" runs through the period's last day instead of today. */
  thisFullPeriod: boolean;
  /** The committed range, used only by the "between" builder. */
  range: BareRange;
}

/**
 * The picker's builder state as a structured selection — the storable form of what the user
 * chose.
 *
 * Derived from the builder (mode + params), never from the resulting dates: a relative choice
 * has to survive as relative so it re-resolves on the next visit, and the dates it resolves to
 * may have been clamped to the data's bounds while the intent never is. Presets feed this
 * function too, since clicking one just sets the builder.
 */
export const builderSelection = (state: BuilderSelectionState): DateRangeSelection => {
  // "All time" is exactly what a custom selection with both bounds open resolves to.
  if (state.isAllTime) {
    return { kind: "custom", start: null, end: null };
  }
  if (state.mode === "last") {
    return {
      kind: "last",
      n: state.lastN,
      unit: state.lastUnit,
      include_current: state.includeCurrent,
    };
  }
  if (state.mode === "this") {
    // The flag is only written when set, so a to-date choice keeps the shape stored specs
    // already carry.
    return state.thisFullPeriod
      ? { kind: "this", unit: state.thisUnit, full_period: true }
      : { kind: "this", unit: state.thisUnit };
  }
  return {
    kind: "custom",
    start: dateToInputValue(state.range.start),
    end: dateToInputValue(state.range.end),
  };
};

export const buildPresets = (selectableBounds: BareRange | null): Preset[] => {
  const today = getToday();
  const presets: Preset[] = [];
  const lastN = (
    id: string,
    label: string,
    n: number,
    unit: SelectionUnit = "day",
    includeCurrent = true
  ) =>
    presets.push({
      id,
      label,
      lastN: n,
      lastUnit: unit,
      lastIncludeCurrent: includeCurrent,
      range: computeLastRange(n, unit, includeCurrent, selectableBounds),
    });
  const thisUnit = (id: string, label: string, unit: ThisUnit) =>
    presets.push({ id, label, thisUnit: unit, range: computeThisRange(unit, selectableBounds) });

  lastN("last-7", "Last 7 days", 7);
  lastN("last-30", "Last 30 days", 30);
  lastN("last-60", "Last 60 days", 60);
  lastN("last-3m", "Last 3 months", 3, "month");
  lastN("last-6m", "Last 6 months", 6, "month");
  lastN("last-12m", "Last 12 months", 12, "month");
  thisUnit("this-month", "This month", "month");
  thisUnit("this-quarter", "This quarter", "quarter");
  thisUnit("this-year", "This year", "year");
  // The previous whole year, expressed in the builder's own vocabulary: one complete year,
  // current one excluded. Computing it as a literal Jan 1 - Dec 31 range instead (as this
  // preset used to) leaves it with no identity beyond its dates, so saving it as a default
  // froze the dashboard on whichever year was current at save time.
  lastN("last-year", "Last year", 1, "year", false);

  // "All time" commits the open sentinel window (all history through everything). It no
  // longer depends on the data's date range, so it is always offered.
  presets.push({
    id: "all-time",
    label: "All time",
    allTime: true,
    range: { start: today, end: today },
  });

  // "Custom" makes the free-form path explicit: it opens the "between" builder on the current
  // draft range instead of committing a range of its own.
  presets.push({
    id: "custom",
    label: "Custom",
    custom: true,
    range: { start: today, end: today },
  });

  return presets;
};
